import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';

export type SearchableSelectOption = {
  value: string;
  label: string;
};

type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  // shown and selectable as "no selection" (value undefined), e.g. "Alle Mitarbeiter"
  emptyOptionLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  className?: string;
};

// case-insensitive substring match on the label, more predictable than fuzzy matching for names
const filterByLabel = (_value: string, search: string, keywords?: string[]) =>
  keywords?.some((k) => k.toLowerCase().includes(search.trim().toLowerCase())) ? 1 : 0;

export const SearchableSelect = ({
  options,
  value,
  onChange,
  emptyOptionLabel,
  placeholder = 'Auswählen',
  searchPlaceholder = 'Suchen...',
  noResultsText = 'Keine Ergebnisse',
  className = '',
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = options.find((o) => o.value === value);
  const triggerLabel = selected?.label ?? emptyOptionLabel ?? placeholder;

  // every open starts with an empty search
  const changeOpen = (next: boolean) => {
    setOpen(next);
    setSearch('');
  };

  const select = (next: string | undefined) => {
    onChange(next);
    changeOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={changeOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={`flex items-center justify-between gap-2 px-3 py-2 border border-[#e0e0e0] rounded-lg bg-white text-left min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent data-[state=open]:ring-2 data-[state=open]:ring-[#0084c7] data-[state=open]:border-transparent ${className}`}
        >
          <span className={`truncate ${selected || emptyOptionLabel ? 'text-[#1c1c1c]' : 'text-[#6b6b6b]'}`}>
            {triggerLabel}
          </span>
          <ChevronDown className="size-4 shrink-0 text-[#6b6b6b]" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          // Escape should only close the dropdown, not a surrounding Modal listening on document
          onEscapeKeyDown={(e) => e.stopPropagation()}
          className="z-[60] w-[var(--radix-popover-trigger-width)] min-w-[220px] rounded-lg border border-[#e0e0e0] bg-white shadow-lg"
        >
          <Command filter={filterByLabel}>
            <div className="flex items-center gap-2 pl-3 border-b border-[#e0e0e0]">
              <Search className="size-4 shrink-0 text-[#6b6b6b]" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={searchPlaceholder}
                className="w-full py-2 text-[14px] outline-none bg-transparent"
              />
            </div>

            <Command.List className="max-h-[280px] overflow-y-auto p-1">
              <Command.Empty className="px-3 py-2 text-[14px] text-[#6b6b6b]">
                {noResultsText}
              </Command.Empty>

              {emptyOptionLabel && (
                <Command.Item
                  value="__empty__"
                  keywords={[emptyOptionLabel]}
                  onSelect={() => select(undefined)}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-[14px] rounded-md cursor-pointer data-[selected=true]:bg-[#f5f5f5]"
                >
                  {emptyOptionLabel}
                  {value === undefined && <Check className="size-4 text-[#0084c7]" />}
                </Command.Item>
              )}

              {options.map((option) => (
                <Command.Item
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => select(option.value)}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-[14px] rounded-md cursor-pointer data-[selected=true]:bg-[#f5f5f5]"
                >
                  <span className="truncate">{option.label}</span>
                  {option.value === value && <Check className="size-4 shrink-0 text-[#0084c7]" />}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
